/**
 * AttachmentPills - the removable file pills on the research composer, each
 * showing its upload status (uploading → ready / failed).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiCheckLight,
  PiPaperclipLight,
  PiWarningCircleLight,
  PiXLight,
} from 'react-icons/pi';
import { ComposerAttachment } from './attachments';
import Spinner from '../shared/TailwindComponents/Spinner';

interface Props {
  attachments: ComposerAttachment[];
  onRemove: (id: string) => void;
}

const AttachmentPills: React.FC<Props> = ({ attachments, onRemove }) => {
  const { t } = useTranslation();
  if (!attachments.length) return null;

  return (
    <div className="mb-2 flex flex-wrap gap-1.5 font-sans" data-testid="attachment-pills">
      {attachments.map((attachment, i) => (
        <span
          key={attachment.id}
          data-testid="attachment-pill"
          data-status={attachment.status}
          title={attachment.error || attachment.name}
          style={{ '--reveal-delay': `${Math.min(i, 6) * 50}ms` } as React.CSSProperties}
          className={clsx(
            'inline-flex items-center gap-1.5 ps-2 pe-1 py-1 rounded-full text-xs reveal-item',
            'bg-surface-sunken border border-hairline-soft transition-colors duration-200 ease-slide',
            attachment.status === 'failed' ? 'text-danger-600' : 'text-fg-body'
          )}
        >
          {attachment.status === 'uploading' ? (
            <Spinner size="glyph" />
          ) : attachment.status === 'failed' ? (
            <PiWarningCircleLight className="w-3.5 h-3.5" aria-hidden />
          ) : (
            <PiPaperclipLight className="w-3.5 h-3.5 text-fg-muted" aria-hidden />
          )}

          <span className="max-w-[160px] truncate">{attachment.name}</span>

          {attachment.status === 'ready' && (
            <PiCheckLight className="w-3 h-3 text-champagne" aria-hidden />
          )}
          {attachment.status === 'uploading' && (
            <span className="text-[10px] text-fg-muted">{t('Uploading')}</span>
          )}
          {attachment.status === 'failed' && (
            <span className="text-[10px]">{t('Failed')}</span>
          )}

          <button
            type="button"
            onClick={() => onRemove(attachment.id)}
            aria-label={`${t('Remove')} ${attachment.name}`}
            className="p-0.5 rounded-full text-fg-muted hover:text-fg-strong transition-colors duration-200 ease-slide"
          >
            <PiXLight className="w-3 h-3" />
          </button>
        </span>
      ))}
    </div>
  );
};

export default AttachmentPills;
